import React, { useState, useMemo, useEffect } from 'react';
import { ComponentBatch } from '../../../types/componentBatch';
import { Recipe } from '../../../types/recipe';
import { ComponentIngredient } from '../../../types/componentRecord';
import { ComponentRecordCard } from './ComponentRecordCard';
import { createBlankComponentBatch } from '../../../store/slices/componentBatchSlice';
import { groupItemsBySection } from '../../../utils/haccpSectionUtils';
import { HaccpSectionHeader } from '../../../components/haccp/HaccpSectionHeader';
import { matchesSearchQuery } from '../../../utils/searchUtils';

interface ComponentRecordListProps {
  records: ComponentBatch[];
  components: Recipe[];
  onAdd: (record: ComponentBatch, ingredients: ComponentIngredient[]) => void;
  onUpdate: (record: ComponentBatch) => void;
  onDelete: (recordId: string) => void;
  onTimesProducedChange?: (id: string, timesProduced: number) => void;
}

export const ComponentRecordList: React.FC<ComponentRecordListProps> = ({ records, components, onAdd, onUpdate, onDelete, onTimesProducedChange }) => {
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newRecord, setNewRecord] = useState<ComponentBatch>(createBlankComponentBatch());
  const [searchTerm, setSearchTerm] = useState('');

  // Filter records based on search
  const filteredRecords = useMemo(() => {
    if (!searchTerm.trim()) return records;
    return records.filter(record => matchesSearchQuery(record, searchTerm, [record.productName || '']));
  }, [records, searchTerm]);

  // Group records into sections by date
  const sections = useMemo(
    () => groupItemsBySection(filteredRecords, (record) => record.dateOfProduction),
    [filteredRecords]
  );

  // Track expanded/collapsed state for each section (all collapsed by default)
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() => {
    const state: Record<string, boolean> = {};
    for (const section of sections) {
      state[section.key] = false;
    }
    return state;
  });

  // Update expanded state when sections change (e.g., new month appears)
  useEffect(() => {
    setExpandedSections((prev) => {
      const newState = { ...prev };
      let hasChanges = false;
      for (const section of sections) {
        if (!(section.key in newState)) {
          newState[section.key] = false;
          hasChanges = true;
        }
      }
      return hasChanges ? newState : prev;
    });
  }, [sections]);

  const toggleSection = (sectionKey: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const handleAddNew = () => {
    setIsAddingNew(true);
    setNewRecord(createBlankComponentBatch());
  };

  const handleSaveNew = (record: ComponentBatch, ingredients: ComponentIngredient[]) => {
    // Validate required fields
    if (!record.productId || !record.dateOfProduction || !record.preparedBy || !record.verifiedBy) {
      alert('Product, Date of Production, Prepared By, and Verified By are required fields.');
      return;
    }

    onAdd({
      ...record,
      updatedAt: new Date().toISOString()
    }, ingredients);
    setIsAddingNew(false);
    setNewRecord(createBlankComponentBatch());
  };

  const handleCancelNew = () => {
    setIsAddingNew(false);
    setNewRecord(createBlankComponentBatch());
  };

  const handleEditToggle = (recordId: string) => {
    if (editingId === recordId) {
      setEditingId(null);
    } else {
      setEditingId(recordId);
    }
  };

  const handleUpdate = (record: ComponentBatch) => {
    // Validate required fields
    if (!record.productId || !record.dateOfProduction || !record.preparedBy || !record.verifiedBy) {
      alert('Product, Date of Production, Prepared By, and Verified By are required fields.');
      return;
    }

    onUpdate({
      ...record,
      updatedAt: new Date().toISOString()
    });
    setEditingId(null);
  };

  const buttonStyle: React.CSSProperties = {
    padding: '8px 16px',
    borderRadius: '4px',
    border: 'none',
    fontWeight: '500',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'background-color 0.2s'
  };

  return (
    <div>
      {/* Add New Component Button */}
      <div style={{ marginBottom: '16px' }}>
        <button
          onClick={handleAddNew}
          disabled={isAddingNew || editingId !== null}
          style={{
            ...buttonStyle,
            backgroundColor: isAddingNew || editingId !== null ? '#cccccc' : '#4CAF50',
            color: 'white',
            cursor: isAddingNew || editingId !== null ? 'not-allowed' : 'pointer'
          }}
          onMouseEnter={e => {
            if (!isAddingNew && editingId === null) {
              e.currentTarget.style.backgroundColor = '#45a049';
            }
          }}
          onMouseLeave={e => {
            if (!isAddingNew && editingId === null) {
              e.currentTarget.style.backgroundColor = '#4CAF50';
            }
          }}
        >
          + Add New Component
        </button>
      </div>

      {/* Search */}
      {!isAddingNew && !editingId && (
        <div style={{ marginBottom: '16px' }}>
          <input
            type="text"
            placeholder="Search component records..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              fontSize: '14px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              outline: 'none',
              boxSizing: 'border-box' as const
            }}
          />
        </div>
      )}

      {/* New Component Record Card */}
      {isAddingNew && (
        <ComponentRecordCard
          record={newRecord}
          components={components}
          isEditMode={true}
          onUpdate={() => {}}
          onSaveNew={handleSaveNew}
          onDelete={handleCancelNew}
          onEditToggle={handleCancelNew}
        />
      )}

      {/* Existing Component Records grouped by section */}
      {records.length === 0 && !isAddingNew && (
        <div
          style={{
            padding: '40px 20px',
            textAlign: 'center',
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            color: '#999',
            fontStyle: 'italic'
          }}
        >
          <p style={{ margin: 0, fontSize: '1rem' }}>No component records yet. Click "Add New Component" to create one.</p>
        </div>
      )}

      {/* No search results */}
      {records.length > 0 && filteredRecords.length === 0 && !isAddingNew && (
        <div
          style={{
            padding: '40px 20px',
            textAlign: 'center',
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            color: '#999',
            fontStyle: 'italic'
          }}
        >
          <p style={{ margin: 0, fontSize: '1rem' }}>No component records match your search for "{searchTerm}".</p>
        </div>
      )}

      {sections.map((section) => (
        <div key={section.key} style={{ marginBottom: '16px' }}>
          <HaccpSectionHeader
            label={section.label}
            itemCount={section.items.length}
            isExpanded={expandedSections[section.key] ?? !section.isTrialSection}
            isTrialSection={section.isTrialSection}
            onToggle={() => toggleSection(section.key)}
          />

          {(expandedSections[section.key] ?? !section.isTrialSection) && (
            <div>
              {section.items.map((record) => (
                <ComponentRecordCard
                  key={record.id}
                  record={record}
                  components={components}
                  isEditMode={editingId === record.id}
                  onUpdate={handleUpdate}
                  onDelete={() => onDelete(record.id)}
                  onEditToggle={() => handleEditToggle(record.id)}
                  onTimesProducedChange={onTimesProducedChange}
                />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
